/**
 * Shared display formatting for coordinates, residuals, angles, and scale.
 *
 * Every formatter accepts `null`, `undefined`, NaN, and ±Infinity and returns
 * the placeholder for them, so components and reports do not need their own
 * guards. Negative zero is printed without a sign ("0.000", not "-0.000").
 */

/** Shown in place of a value that is missing or not finite. */
export const PLACEHOLDER = '—';

export type Numeric = number | null | undefined;

const isFiniteNumber = (value: Numeric): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Format a number with a fixed number of decimals.
 *
 * @param value - Number to format; missing or non-finite gives the placeholder
 * @param decimals - Decimal places (0–20)
 * @param options.signed - Prefix positive values (and zero) with "+"
 * @param options.placeholder - Text for missing values (default "—")
 */
export const formatFixed = (
  value: Numeric,
  decimals: number,
  options: { signed?: boolean; placeholder?: string } = {}
): string => {
  const { signed = false, placeholder = PLACEHOLDER } = options;
  if (!isFiniteNumber(value)) return placeholder;
  let text = value.toFixed(decimals);
  // toFixed keeps the sign of tiny negatives that round to zero.
  if (/^-0(\.0+)?$/.test(text)) text = text.slice(1);
  return signed && !text.startsWith('-') ? `+${text}` : text;
};

/**
 * Format degrees as signed degrees-minutes-seconds, e.g. `+1° 30' 45.5"`.
 *
 * Seconds are rounded to the display precision before the value is split,
 * so 0.99999999° becomes `+1° 00' 00.0"` rather than `+0° 59' 60.0"`.
 */
export const formatDMS = (degrees: Numeric, secondsDecimals = 1): string => {
  if (!isFiniteNumber(degrees)) return PLACEHOLDER;
  const scale = 10 ** secondsDecimals;
  const totalUnits = Math.round(Math.abs(degrees) * 3600 * scale);
  const sign = degrees < 0 && totalUnits > 0 ? '-' : '+';
  const unitsPerMinute = 60 * scale;
  const d = Math.floor(totalUnits / (60 * unitsPerMinute));
  const m = Math.floor(totalUnits / unitsPerMinute) % 60;
  const s = (totalUnits % unitsPerMinute) / scale;
  const width = secondsDecimals > 0 ? secondsDecimals + 3 : 2;
  const seconds = s.toFixed(secondsDecimals).padStart(width, '0');
  return `${sign}${d}° ${String(m).padStart(2, '0')}' ${seconds}"`;
};

/**
 * Format an azimuth as whole degrees clockwise from north in [0°, 360°),
 * e.g. `45°`. The value is truncated, matching the coarse direction label
 * used beside residual vectors.
 */
export const formatAzimuth = (degrees: Numeric): string => {
  if (!isFiniteNumber(degrees)) return PLACEHOLDER;
  const normalized = ((degrees % 360) + 360) % 360;
  return `${Math.floor(normalized) % 360}°`;
};

/**
 * Format a coordinate with fixed decimals (default 4). Strings are parsed at
 * this display boundary; stored coordinate strings are never modified.
 */
export const formatCoordinate = (
  value: Numeric | string,
  decimals = 4
): string =>
  formatFixed(
    typeof value === 'string' ? parseCoordinate(value) : value,
    decimals
  );

/** Format a residual with an explicit sign (default 3 decimals). */
export const formatResidual = (value: Numeric, decimals = 3): string =>
  formatFixed(value, decimals, { signed: true });

/** Format a scale factor (default 8 decimals). */
export const formatScale = (value: Numeric, decimals = 8): string =>
  formatFixed(value, decimals);

/** Convert a scale factor to its departure from unity in parts per million. */
export const scaleToPpm = (scale: number): number => (scale - 1) * 1e6;

/** Format a scale factor as signed parts per million, e.g. `+50.0 ppm`. */
export const formatPpm = (scale: Numeric, decimals = 1): string =>
  isFiniteNumber(scale)
    ? `${formatFixed(scaleToPpm(scale), decimals, { signed: true })} ppm`
    : PLACEHOLDER;

/**
 * Format the confidence level implied by a two-sided significance level,
 * e.g. alpha 0.001 → `99.9%`.
 */
export const formatConfidence = (alpha: Numeric, decimals = 1): string =>
  isFiniteNumber(alpha)
    ? `${formatFixed((1 - alpha) * 100, decimals)}%`
    : PLACEHOLDER;

/**
 * Parse a coordinate string to a number; NaN when it is not numeric.
 */
export const parseCoordinate = (value: string): number => {
  const num = parseFloat(value.trim());
  return isNaN(num) ? NaN : num;
};
